import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    AreaChart,
    Area,
    ComposedChart
} from "recharts";
import { getUserActivityAnalytics } from "../../api/context/analytics";
import { FiActivity, FiUsers, FiClock, FiShoppingCart, FiEye, FiDollarSign, FiRefreshCw, FiStore, FiTag, FiGrid, FiPackage, FiSearch, FiList, FiAlertTriangle, FiMapPin, FiGift } from "react-icons/fi";
import Loader from '../../components/loader/loader';
import toast from "react-hot-toast";
import { FaStore } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

const COLORS = ["#6366F1", "#22C55E", "#FACC15", "#F97316", "#EC4899", "#06B6D4", "#8B5CF6", "#F43F5E"];

const AnalyticsDashboard = () => {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const getDefaultDates = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
        };
    };

    const [filters, setFilters] = useState(getDefaultDates());

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const formatActivityType = (type) => {
        const types = {
            'cart_add': 'Cart Adds',
            'store_view': 'Store Views',
            'purchase': 'Purchases',
            'product_view': 'Product Views',
            'brand_view': 'Brand Views',
            'category_view': 'Category Views',
            'search': 'Searches'
        };
        return types[type] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const formatTimeSpent = (minutes) => {
        if (minutes < 60) {
            return `${Math.round(minutes)} min`;
        }
        const hours = (minutes / 60).toFixed(1);
        return `${hours} hrs`;
    };

    const fetchAnalytics = async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        const requestBody = {
            startDate: filters.startDate,
            endDate: filters.endDate
        };

        console.log("📊 User Activity params:", requestBody);

        try {
            await new Promise((resolve, reject) => {
                getUserActivityAnalytics(requestBody)((response) => {
                    if (response?.statusCode === 200) {
                        setAnalytics(response?.data);

                        const start = new Date(filters.startDate);
                        const end = new Date(filters.endDate);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

                        toast.success(`Activity data fetched for ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`);
                        resolve(response);
                    } else {
                        setAnalytics(null);
                        reject(new Error('Failed to fetch activity analytics'));
                    }
                });
            });
        } catch (error) {
            console.error("Error fetching activity analytics:", error);
            setAnalytics(null);
            toast.error('Failed to fetch activity data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const handleFilterChange = (key, value) => {
        const today = getTodayDate();

        if (key === 'startDate' && value > today) {
            toast.error('Start date cannot be in the future');
            return;
        }

        if (key === 'endDate' && value > today) {
            toast.error('End date cannot be in the future');
            return;
        }

        if (key === 'startDate' && filters.endDate && value > filters.endDate) {
            toast.error('Start date cannot be after end date');
            return;
        }

        if (key === 'endDate' && filters.startDate && value < filters.startDate) {
            toast.error('End date cannot be before start date');
            return;
        }

        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    useEffect(() => {
        const hasBothDates = filters.startDate && filters.endDate;
        const isDefaultState = JSON.stringify(filters) === JSON.stringify(getDefaultDates());

        if (hasBothDates && !isDefaultState) {
            const timer = setTimeout(() => {
                fetchAnalytics(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [filters]);

    const handleClearFilters = () => {
        const defaultDates = getDefaultDates();
        setFilters(defaultDates);

        setTimeout(() => {
            setRefreshing(true);
            const requestBody = {
                startDate: defaultDates.startDate,
                endDate: defaultDates.endDate
            };

            getUserActivityAnalytics(requestBody)((response) => {
                if (response?.statusCode === 200) {
                    setAnalytics(response?.data);
                } else {
                    setAnalytics(null);
                    toast.error('Failed to fetch activity data');
                }
                setRefreshing(false);
            });
        }, 100);
    };

    const isFilterActive = () => {
        const defaultDates = getDefaultDates();
        return filters.startDate !== defaultDates.startDate || filters.endDate !== defaultDates.endDate;
    };

    // Helper function to get activity data for specific types
    const getActivityData = (type) => {
        if (!analytics?.activityBreakdown) return null;
        return analytics.activityBreakdown.find(activity => activity.activityType === type);
    };

    if (loading && !refreshing) return (

        <Loader />

    );

    if (!loading && !analytics) {
        return (
            <div className="flex flex-col items-center justify-center p-10 text-center text-gray-600">
                <FiActivity className="text-5xl mb-3 text-gray-400" />
                <h2 className="font-bold text-2xl">No Activity Data Available</h2>
                <p className="mt-2 text-lg text-gray-500 max-w-md">
                    No user activity data found for the selected period.
                    Try adjusting the date range or check back later.
                </p>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4 min-vh-100">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                    <h3 className="mb-0 fw-bolder">Analytics Dashboard</h3>
                    <p className="text-muted mb-4">
                        Click on activity cards to explore detailed analytics pages.
                    </p>
                </div>

                <div className="d-flex align-items-center gap-3">
                    {/* Date Filters */}
                    <div className="d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                style={{ width: '140px' }}
                                placeholder="Start Date"
                                max={filters.endDate || getTodayDate()}
                            />
                            <div className="text-muted small">to</div>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                style={{ width: '140px' }}
                                placeholder="End Date"
                                min={filters.startDate}
                                max={getTodayDate()}
                            />
                        </div>
                        {isFilterActive() && (
                            <button
                                onClick={handleClearFilters}
                                className="btn btn-sm btn-outline-secondary"
                                disabled={refreshing}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    <button
                        onClick={() => fetchAnalytics(true)}
                        className="btn border border-secondary bg-transparent rounded-3 p-2"
                        style={{
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        disabled={refreshing}
                    >
                        <FiRefreshCw
                            className="text-secondary"
                            size={18}
                            style={{
                                animation: refreshing ? 'spin 1s linear infinite' : 'none'
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Activity Type Cards - Horizontal Scroll */}
            <div className="row g-4 mb-3">
                <div className="col-12">
                    <div className="d-flex overflow-auto gap-3" style={{ minHeight: '120px' }}>
                        {[
                            {
                                title: "Store",
                                icon: <FaStore className="text-primary" />,
                                type: "store_view",
                                color: "#6366F1",
                                bgColor: "rgba(99, 102, 241, 0.1)",
                                navigateTo: "/store-analytics",
                                enabled: true
                            },
                            {
                                title: "Cart",
                                icon: <FiShoppingCart className="text-warning" />,
                                type: "cart_add",
                                color: "#F97316",
                                bgColor: "rgba(249, 115, 22, 0.1)",
                                navigateTo: "/cart-analytics",
                                enabled: true
                            },
                            {
                                title: "Purchase",
                                icon: <FiDollarSign className="text-success" />,
                                type: "purchase",
                                color: "#22C55E",
                                bgColor: "rgba(34, 197, 94, 0.1)",
                                navigateTo: "/purchase-analytics",
                                enabled: true
                            },
                            {
                                title: "Brand",
                                icon: <FiTag className="text-danger" />,
                                type: "brand_view",
                                color: "#F43F5E",
                                bgColor: "rgba(244, 63, 94, 0.1)",
                                navigateTo: "/brand-analytics",
                                enabled: true
                            },
                            {
                                title: "Category",
                                icon: <FiGrid className="text-info" />,
                                type: "category_view",
                                color: "#06B6D4",
                                bgColor: "rgba(6, 182, 212, 0.1)",
                                navigateTo: "/category-analytics",
                                enabled: true
                            },
                            {
                                title: "Product",
                                icon: <FiPackage className="text-purple" />,
                                type: "product_view",
                                color: "#8B5CF6",
                                bgColor: "rgba(139, 92, 246, 0.1)",
                                navigateTo: "/product-analytics",
                                enabled: true
                            },

                        ].map((card, i) => {
                            const activityData = getActivityData(card.type);
                            if (!activityData && card.type !== "brand_view" && card.type !== "category_view" && card.type !== "product_view" && card.type !== "search" && card.type !== "location") {
                                return null;
                            }

                            const handleCardClick = () => {
                                if (card.navigateTo === '/search-analytics' || card.navigateTo === '/location-analytics') {
                                    navigate(card.navigateTo);
                                } else {
                                    toast.error(`${card.title} analytics page is coming soon!`);
                                }
                            };

                            return (
                                <div
                                    key={i}
                                    className="card h-100 border border-secondary rounded-3"
                                    style={{
                                        minWidth: '230px',
                                        flexShrink: 0,
                                        cursor: card.enabled ? 'pointer' : 'not-allowed',
                                        opacity: card.enabled ? 1 : 0.7
                                    }}
                                    onClick={handleCardClick}
                                >
                                    <div className="card-body p-3 d-flex flex-column">
                                        {/* Heading with icon on right - top row */}
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h6 className="text-muted mb-0 fw-semibold">{card.title}</h6>
                                            {React.cloneElement(card.icon, {
                                                size: 16,
                                                className: "text-secondary opacity-75"
                                            })}
                                        </div>

                                        {/* Main content */}
                                        <div className="mt-auto">
                                            {/* Total Time in Bold */}
                                            <h4 className="fw-bold mb-1">
                                                {activityData?.totalTimeSpentMinutes ?
                                                    formatTimeSpent(activityData.totalTimeSpentMinutes) : "0 min"}
                                            </h4>

                                            {/* Views and Users in small light text */}
                                            <div className="small text-muted">
                                                <span className="me-2">
                                                    {activityData?.count || "0"} event
                                                </span>
                                                <span>
                                                    {activityData?.uniqueUsers || "0"} users
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Search, Location & Promo Section */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 shadow-sm rounded-3">
                        <h5 className="fw-bold mb-4">Search, Location & Promo Insights</h5>

                        <div className="row g-4">
                            {/* Search Card */}
                            <div className="col-md-4">
                                <div
                                    className="border border-secondary rounded-3 p-4 h-100 d-flex align-items-center justify-content-between"
                                    style={{ cursor: "pointer", transition: "0.2s" }}
                                    onClick={() => navigate('/search-analytics')}
                                    onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                >
                                    <div className="d-flex align-items-center gap-3">
                                        <div
                                            className="d-flex align-items-center justify-content-center border rounded-3"
                                            style={{ width: 40, height: 40 }}
                                        >
                                            <FiSearch size={18} className="text-muted" />
                                        </div>

                                        <div>
                                            <h6 className="mb-1 fw-bold">Search Analytics</h6>
                                            <small className="text-muted">
                                                Explore user search behavior and trends
                                            </small>
                                        </div>
                                    </div>

                                  
                                </div>
                            </div>

                            {/* Location Card */}
                            <div className="col-md-4">
                                <div
                                    className="border border-secondary rounded-3 p-4 h-100 d-flex align-items-center justify-content-between"
                                    style={{ cursor: "pointer", transition: "0.2s" }}
                                    onClick={() => navigate('/location-analytics')}
                                    onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                >
                                    <div className="d-flex align-items-center gap-3">
                                        <div
                                            className="d-flex align-items-center justify-content-center border rounded-2"
                                            style={{ width: 40, height: 40 }}
                                        >
                                            <FiMapPin size={18} className="text-muted" />
                                        </div>

                                        <div>
                                            <h6 className="mb-1 fw-bold">Location Analytics</h6>
                                            <small className="text-muted">
                                                Geographic user interactions
                                            </small>
                                        </div>
                                    </div>

                                    
                                </div>
                            </div>

                            {/* Promo Card */}
                            <div className="col-md-4">
                                <div
                                    className="border border-secondary rounded-3 p-4 h-100 d-flex align-items-center justify-content-between"
                                    style={{ cursor: "pointer", transition: "0.2s" }}
                                    onClick={() => navigate('/promo-analytics')}
                                    onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                >
                                    <div className="d-flex align-items-center gap-3">
                                        <div
                                            className="d-flex align-items-center justify-content-center border rounded-2"
                                            style={{ width: 40, height: 40 }}
                                        >
                                            <FiGift size={18} className="text-muted" />
                                        </div>

                                        <div>
                                            <h6 className="mb-1 fw-bold">Promo Analytics</h6>
                                            <small className="text-muted">
                                                Track promocode redemptions
                                            </small>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity Trends Chart - Full Width */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 rounded-3 shadow-sm mb-4">
                        <div className="card-body p-0">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="fw-bold my-2">Activity Trends Over Time</h5>
                                <div className="d-flex gap-2">
                                    <span className="badge" style={{ backgroundColor: '#00a2baff', color: 'white' }}>
                                        <FiActivity size={12} className="me-1" /> Total Events
                                    </span>
                                    <span className="badge " style={{ backgroundColor: '#ae1bf3ff', color: 'white' }}>
                                        <FiUsers size={12} className="me-1" /> Unique Users
                                    </span>
                                </div>
                            </div>
                            <div style={{ height: 350, backgroundColor: '#f8f9fa', borderRadius: '0.75rem', paddingTop: '2rem', paddingBottom: '2rem' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={analytics?.activityTrends || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 12 }}
                                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip
                                            formatter={(value, name) => {
                                                // Map the names properly
                                                if (name === 'totalEvents') {
                                                    return [value, 'Total Events'];
                                                } else if (name === 'uniqueUsers') {
                                                    return [value, 'Unique Users'];
                                                }
                                                return [value, name];
                                            }}
                                            labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                            contentStyle={{
                                                backgroundColor: 'white',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                                                padding: '12px 16px',
                                                fontSize: '14px'
                                            }}
                                            itemStyle={{
                                                padding: '4px 0'
                                            }}
                                            labelStyle={{
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#374151'
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="totalEvents"
                                            name="Total Events"
                                            stroke="#00a2baff" // Blue color
                                            strokeWidth={3}
                                            dot={{ strokeWidth: 2, r: 4, fill: '#00a2baff' }}
                                            activeDot={{ r: 6, fill: '#00a2baff' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="uniqueUsers"
                                            name="Unique Users"
                                            stroke="#ae1bf3ff" // Purple color
                                            strokeWidth={3}
                                            dot={{ strokeWidth: 2, r: 4, fill: '#ae1bf3ff' }}
                                            activeDot={{ r: 6, fill: '#ae1bf3ff' }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Top Performing Stores, Brands, Categories, Products Sections */}
            <div>
                {/* Top Stores Section - Only show if there's data */}
                {analytics?.topStores && analytics.topStores.length > 0 && (
                    <div className="row mb-2">
                        <div className="col-12">
                            <div className="card border-0 rounded-3 shadow-sm">
                                <div className="card-body p-0">
                                    {/* Header */}
                                    <div className="d-flex justify-content-between align-items-center pb-4 pt-4">
                                        <div className="d-flex align-items-center">
                                            <h5 className="fw-bold mb-0">Top Performing Stores</h5>
                                        </div>
                                        <small className="text-muted">{analytics.topStores.length} stores</small>
                                    </div>

                                    {/* Horizontal Scroll Cards */}
                                    <div className="d-flex overflow-auto px-6 pb-6" style={{ gap: '1rem', minHeight: '140px' }}>
                                        {analytics.topStores.map((store, index) => (
                                            <div key={store.id} className="card border-0 rounded-3 shadow bg-light" style={{ minWidth: '300px', flexShrink: 0 }}>
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-start">
                                                        {/* Image with white # badge on LEFT */}
                                                        <div className="position-relative me-3 flex-shrink-0">
                                                            <span className="badge bg-white text-dark position-absolute top-0 start-0 translate-middle"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    padding: '3px 16px',
                                                                    fontWeight: '600',
                                                                    zIndex: 1
                                                                }}>
                                                                #{index + 1}
                                                            </span>
                                                            {store.image ? (
                                                                <img
                                                                    src={store.image}
                                                                    alt={store.name}
                                                                    className="rounded-2"
                                                                    style={{
                                                                        width: 100,
                                                                        height: 100,
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="rounded-2 d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: 100,
                                                                        height: 100,
                                                                        backgroundColor: '#f8f9fa',
                                                                        color: '#6c757d'
                                                                    }}>
                                                                    <FaStore size={40} className="opacity-50" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Content */}
                                                        <div className="flex-grow-1">
                                                            {/* Store Name */}
                                                            <h6 className="fw-semibold mb-1 text-truncate"
                                                                style={{
                                                                    maxWidth: '150px',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                {store.name}
                                                            </h6>

                                                            {/* Time */}
                                                            <h4 className="fw-bold mb-3" style={{ fontSize: '28px', color: '#202020ff' }}>
                                                                {formatTimeSpent(store.timeSpentMinutes)}
                                                            </h4>

                                                            {/* Views and Users with icons */}
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="d-flex align-items-center">
                                                                    <FiEye size={14} className="text-muted me-1" />
                                                                    <span className="small text-muted">{store.views} events</span>
                                                                </div>
                                                                <div className="d-flex align-items-center">
                                                                    <FiUsers size={14} className="text-muted me-1" />
                                                                    <span className="small text-muted">{store.uniqueUsers} users</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Top Brands Section - Only show if there's data */}
                {analytics?.topBrands && analytics.topBrands.length > 0 && (
                    <div className="row my-2">
                        <div className="col-12">
                            <div className="card border-0 rounded-3 shadow-sm">
                                <div className="card-body p-0">
                                    {/* Header */}
                                    <div className="d-flex justify-content-between align-items-center pb-4 pt-4">
                                        <h5 className="fw-bold mb-0">Top Performing Brands</h5>
                                        <small className="text-muted">{analytics.topBrands.length} brands</small>
                                    </div>

                                    {/* Horizontal Scroll Cards */}
                                    <div className="d-flex overflow-auto px-6 pb-6" style={{ gap: '1rem', minHeight: '140px' }}>
                                        {analytics.topBrands.map((brand, index) => (
                                            <div key={brand.id} className="card border-0 rounded-3 shadow bg-light" style={{ minWidth: '300px', flexShrink: 0 }}>
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-start">
                                                        {/* Image with white # badge on LEFT */}
                                                        <div className="position-relative me-3 flex-shrink-0">
                                                            <span className="badge bg-white text-dark position-absolute top-0 start-0 translate-middle"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    padding: '3px 16px',
                                                                    fontWeight: '600',
                                                                    zIndex: 1
                                                                }}>
                                                                #{index + 1}
                                                            </span>
                                                            {brand.image ? (
                                                                <img
                                                                    src={brand.image}
                                                                    alt={brand.name}
                                                                    className="rounded-2"
                                                                    style={{
                                                                        width: 100,
                                                                        height: 100,
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="rounded-2 d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: 100,
                                                                        height: 100,
                                                                        backgroundColor: '#f8f9fa',
                                                                        color: '#6c757d'
                                                                    }}>
                                                                    <FiTag size={40} className="opacity-50" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Content */}
                                                        <div className="flex-grow-1">
                                                            {/* Brand Name */}
                                                            <h6 className="fw-semibold mb-1 text-truncate"
                                                                style={{
                                                                    maxWidth: '150px',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                {brand.name}
                                                            </h6>

                                                            {/* Time */}
                                                            <h4 className="fw-bold mb-3" style={{ fontSize: '28px', color: '#202020ff' }}>
                                                                {brand.timeSpentMinutes ? formatTimeSpent(brand.timeSpentMinutes) : "N/A"}
                                                            </h4>

                                                            {/* Views and Users with icons */}
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="d-flex align-items-center">
                                                                    <FiEye size={14} className="text-muted me-1" />
                                                                    <span className="small text-muted">{brand.views || 0} events</span>
                                                                </div>
                                                                <div className="d-flex align-items-center">
                                                                    <FiUsers size={14} className="text-muted me-1" />
                                                                    <span className="small text-muted">{brand.uniqueUsers || 0} users</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Top Categories Section - Only show if there's data */}
                {analytics?.topCategories && analytics.topCategories.length > 0 && (
                    <div className="row my-2">
                        <div className="col-12">
                            <div className="card border-0 rounded-3 shadow-sm">
                                <div className="card-body p-0">
                                    {/* Header */}
                                    <div className="d-flex justify-content-between align-items-center pb-4 pt-4">
                                        <div className="d-flex align-items-center">
                                            <h5 className="fw-bold mb-0">Top Performing Categories</h5>
                                        </div>
                                        <small className="text-muted">{analytics.topCategories.length} categories</small>
                                    </div>

                                    {/* Horizontal Scroll Cards */}
                                    <div className="d-flex overflow-auto px-6 pb-6" style={{ gap: '1rem', minHeight: '140px' }}>
                                        {analytics.topCategories.map((category, index) => (
                                            <div key={category.id} className="card border-0 rounded-3 shadow bg-light" style={{ minWidth: '300px', flexShrink: 0 }}>
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-start">
                                                        {/* Image with white # badge on LEFT */}
                                                        <div className="position-relative me-3 flex-shrink-0">
                                                            <span className="badge bg-white text-dark position-absolute top-0 start-0 translate-middle"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    padding: '3px 16px',
                                                                    fontWeight: '600',
                                                                    zIndex: 1
                                                                }}>
                                                                #{index + 1}
                                                            </span>
                                                            {category.image ? (
                                                                <img
                                                                    src={category.image}
                                                                    alt={category.name}
                                                                    className="rounded-2"
                                                                    style={{
                                                                        width: 100,
                                                                        height: 100,
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="rounded-2 d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: 100,
                                                                        height: 100,
                                                                        backgroundColor: '#f8f9fa',
                                                                        color: '#6c757d'
                                                                    }}>
                                                                    <FiGrid size={40} className="opacity-50" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Content */}
                                                        <div className="flex-grow-1">
                                                            {/* Category Name */}
                                                            <h6 className="fw-semibold mb-1 text-truncate"
                                                                style={{
                                                                    maxWidth: '150px',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                {category.name}
                                                            </h6>

                                                            {/* Time */}
                                                            <h4 className="fw-bold mb-3" style={{ fontSize: '28px', color: '#202020ff' }}>
                                                                {category.timeSpentMinutes ? formatTimeSpent(category.timeSpentMinutes) : "N/A"}
                                                            </h4>

                                                            {/* Views and Users with icons */}
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="d-flex align-items-center">
                                                                    <FiEye size={14} className="text-muted me-1" />
                                                                    <span className="small text-muted">{category.views || 0} events</span>
                                                                </div>
                                                                <div className="d-flex align-items-center">
                                                                    <FiUsers size={14} className="text-muted me-1" />
                                                                    <span className="small text-muted">{category.uniqueUsers || 0} users</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Top Products Section - Only show if there's data */}
                {analytics?.topProducts && analytics.topProducts.length > 0 && (
                    <div className="row my-2">
                        <div className="col-12">
                            <div className="card border-0 rounded-3 shadow-sm">
                                <div className="card-body p-0">
                                    {/* Header */}
                                    <div className="d-flex justify-content-between align-items-center pb-4 pt-4">
                                        <div className="d-flex align-items-center">
                                            <h5 className="fw-bold mb-0">Top Performing Products</h5>
                                        </div>
                                        <small className="text-muted">{analytics.topProducts.length} products</small>
                                    </div>

                                    {/* Horizontal Scroll Cards */}
                                    <div className="d-flex overflow-auto px-6 pb-6" style={{ gap: '1rem', minHeight: '140px' }}>
                                        {analytics.topProducts.map((product, index) => (
                                            <div key={product.id} className="card border-0 rounded-3 shadow bg-light" style={{ minWidth: '300px', flexShrink: 0 }}>
                                                <div className="card-body p-3">
                                                    <div className="d-flex align-items-start">
                                                        {/* Image with white # badge on LEFT */}
                                                        <div className="position-relative me-3 flex-shrink-0">
                                                            <span className="badge bg-white text-dark position-absolute top-0 start-0 translate-middle"
                                                                style={{
                                                                    fontSize: '10px',
                                                                    padding: '3px 16px',
                                                                    fontWeight: '600',
                                                                    zIndex: 1
                                                                }}>
                                                                #{index + 1}
                                                            </span>
                                                            {product.image ? (
                                                                <img
                                                                    src={product.image}
                                                                    alt={product.name}
                                                                    className="rounded-2"
                                                                    style={{
                                                                        width: 100,
                                                                        height: 100,
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="rounded-2 d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: 100,
                                                                        height: 100,
                                                                        backgroundColor: '#f8f9fa',
                                                                        color: '#6c757d'
                                                                    }}>
                                                                    <FiPackage size={40} className="opacity-50" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Content */}
                                                        <div className="flex-grow-1">
                                                            {/* Product Name */}
                                                            <h6 className="fw-semibold mb-1 text-truncate"
                                                                style={{
                                                                    maxWidth: '150px',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}>
                                                                {product.name}
                                                            </h6>

                                                            {/* Time */}
                                                            <h4 className="fw-bold mb-3" style={{ fontSize: '28px', color: '#202020ff' }}>
                                                                {product.timeSpentMinutes ? formatTimeSpent(product.timeSpentMinutes) : "N/A"}
                                                            </h4>

                                                            {/* Views and Users with icons */}
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="d-flex align-items-center">
                                                                    <FiEye size={14} className="text-muted me-1" />
                                                                    <span className="small text-muted">{product.views || 0} events</span>
                                                                </div>
                                                                <div className="d-flex align-items-center">
                                                                    <FiUsers size={14} className="text-muted me-1" />
                                                                    <span className="small text-muted">{product.uniqueUsers || 0} users</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Scrollbar Hide CSS */}
                <style>
                    {`
    .overflow-auto::-webkit-scrollbar {
        height: 0px;
    }
    .overflow-auto::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }
    .overflow-auto::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;
    }
    .overflow-auto::-webkit-scrollbar-thumb:hover {
        background: #aaa;
    }
    `}
                </style>
            </div>



        </div>
    );
};

export default AnalyticsDashboard;
